import { OrganizationMember, ProviderAccount, User } from '@prisma/client'
import { message } from 'antd'
import { Api } from '~/core/trpc'
import { useWordpressSdk } from './useWordpressSdk'

type Props = {
  user: User
  organizationMember: OrganizationMember
}

type PublishOptions = {
  blogPostId: string
  providerAccount: ProviderAccount
}

type CreateConnectionOptions = {
  wordpressUrl: string
  username: string
  applicationPassword: string
  publishStatus: 'publish' | 'draft'
}

type SetUserInformation = {
  providerAccountId: string
}

type FindUserInformationOptions = {
  providerAccountId: string
}

type DeleteConnectionOptions = {
  providerAccountId: string
}

export const useWordpress = ({ user, organizationMember }: Props) => {
  const providerAccountCreateMutation = Api.providerAccount.create.useMutation()
  const providerAccountUpdateMutation = Api.providerAccount.update.useMutation()
  const providerAccountDeleteMutation = Api.providerAccount.delete.useMutation()
  const sdk = useWordpressSdk({ organizationMember })

  const providerAccounts = Api.providerAccount.findMany.useQuery(
    {
      where: {
        providerKey: 'wordpress',
        userId: user.id,
      },
      orderBy: {
        updatedAt: 'desc',
      },
    },
    { initialData: [] },
  )

  const publish = async (options: PublishOptions) => {
    const { blogPostId, providerAccount } = options
    try {
      await sdk.publishPost({
        blogPostId,
        providerAccountId: providerAccount.id,
      })
    } catch (error) {
      message.error('something went wrong')
      console.error(error)
    }
  }

  const findUserInformation = async (options: FindUserInformationOptions) => {
    try {
      const { providerAccountId } = options

      return sdk.findUserInformation({
        providerAccountId,
      })
    } catch (error) {
      message.error('something went wrong')
      console.error(error)
    }
  }

  const checkConnection = async (options: SetUserInformation) => {
    if (providerAccountUpdateMutation.isLoading) {
      return
    }

    const { providerAccountId } = options

    try {
      const { response } = await sdk.findUserInformation({
        providerAccountId,
      })

      if (response.BAD_REQUEST) {
        await providerAccountUpdateMutation.mutateAsync({
          where: { id: providerAccountId },
          data: { status: 'CORRUPTED' },
        })

        await providerAccounts.refetch()

        return { success: false }
      }

      await providerAccountUpdateMutation.mutateAsync({
        where: { id: providerAccountId },
        data: {
          pictureUrl: response?.data?.avatar_urls?.['48'],
          status: 'ACTIVE',
        },
      })

      await providerAccounts.refetch()

      return { success: true }
    } catch (error) {
      console.error(error)
      return { success: false }
    }
  }

  const createOrUpdateConnection = async (options: CreateConnectionOptions) => {
    const existingAccount = providerAccounts.data.find(
      account => account.idExternal === options.username,
    )

    const wordpressUrl = options.wordpressUrl?.replace(/\/+$/, '').toLowerCase()

    const connectionSafe: CreateConnectionOptions = {
      ...options,
      wordpressUrl,
    }

    let providerAccount

    if (existingAccount) {
      providerAccount = providerAccountUpdateMutation.mutateAsync({
        where: { id: existingAccount.id },
        data: {
          connection: connectionSafe,
          name: wordpressUrl,
        },
      })
    } else {
      providerAccount = await providerAccountCreateMutation.mutateAsync({
        data: {
          connection: connectionSafe,
          idExternal: options.username,
          name: options.username,
          userId: user.id,
          providerKey: 'wordpress',
        },
      })
    }

    await providerAccounts.refetch()

    return providerAccount
  }

  const deleteConnection = async (options: DeleteConnectionOptions) => {
    if (providerAccountDeleteMutation.isLoading) {
      return
    }

    try {
      await providerAccountDeleteMutation.mutateAsync({
        where: { id: options.providerAccountId },
      })

      await providerAccounts.refetch()
    } catch (error) {
      message.error('something went wrong')
      console.error(error)
    }
  }

  return {
    providerAccounts: providerAccounts.data,
    publish,
    createOrUpdateConnection,
    checkConnection,
    findUserInformation,
    deleteConnection,
    refetch: providerAccounts.refetch,
  }
}
